import { useEffect, useMemo, useState } from "react";
import { Loader2, Minus, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/UserBits";
import { PointsUserSheet } from "@/components/PointsUserSheet";

const fmt = (n) => (n || 0).toLocaleString("de-DE");

function Row({ u, onChange, onOpen }) {
  const [busy, setBusy] = useState(false);
  const adjust = async (amount) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/points`, { amount, reason: amount > 0 ? "Admin: +100 Punkte" : "Admin: -100 Punkte" });
      onChange(u.user_id, data.points);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`points-row-${u.user_id}`}>
      <button onClick={() => onOpen(u.user_id)} data-testid={`points-open-${u.user_id}`} className="flex-1 min-w-0 flex items-center gap-3 text-left">
        <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-9 h-9" noStory />
        <span className="min-w-0">
          <span className="block font-bold text-sm truncate">{u.name}</span>
          <span className="block text-xs text-slate-400 truncate">Verlauf & eigener Betrag ›</span>
        </span>
      </button>
      <button onClick={() => adjust(-100)} disabled={busy || (u.points || 0) < 100} data-testid={`points-minus-${u.user_id}`} aria-label="100 Punkte abziehen"
        className="w-9 h-9 rounded-full border-2 border-rose-200 text-rose-500 flex items-center justify-center hover:bg-rose-50 active:scale-90 transition-transform disabled:opacity-30"><Minus className="w-4 h-4" /></button>
      <span className="w-20 text-center font-extrabold tabular-nums text-rose-600" data-testid={`points-value-${u.user_id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : fmt(u.points)}</span>
      <button onClick={() => adjust(100)} disabled={busy} data-testid={`points-plus-${u.user_id}`} aria-label="100 Punkte schenken"
        className="w-9 h-9 rounded-full pink-gradient text-white flex items-center justify-center active:scale-90 transition-transform disabled:opacity-50"><Plus className="w-4 h-4" /></button>
    </li>
  );
}

export function PointsBalanceAdmin() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(formatErr(e))); }, []);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (users || []).filter((u) => !s || `${u.name} ${u.email}`.toLowerCase().includes(s)).sort((a, b) => (b.points || 0) - (a.points || 0));
  }, [users, q]);
  const onChange = (id, points) => setUsers((l) => l.map((u) => (u.user_id === id ? { ...u, points } : u)));
  if (!users) return <Loader2 className="w-6 h-6 animate-spin text-rose-400 mx-auto" />;
  return (
    <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-4" data-testid="points-balance-admin">
      <div>
        <h2 className="text-lg font-extrabold">Punktestand aller Mitglieder</h2>
        <p className="text-sm text-slate-500">Mit + schenkst du 100 Punkte, mit − ziehst du 100 ab. Tippe auf ein Mitglied für den Punkte-Verlauf oder einen eigenen Betrag mit Grund. Das Mitglied bekommt jeweils eine Mitteilung.</p>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name oder E-Mail suchen …" className="pl-9 rounded-full" data-testid="points-search" />
      </div>
      <p className="text-xs text-slate-400" data-testid="points-count">{list.length} Mitglieder</p>
      <ul className="divide-y divide-pink-50">{list.slice(0, 300).map((u) => <Row key={u.user_id} u={u} onChange={onChange} onOpen={setOpenId} />)}</ul>
      <PointsUserSheet u={users.find((u) => u.user_id === openId)} onOpenChange={(o) => !o && setOpenId(null)} onChange={onChange}
        onPatch={(id, patch) => setUsers((l) => l.map((u) => (u.user_id === id ? { ...u, ...patch } : u)))} />
      {list.length > 300 && <p className="text-xs text-slate-400 text-center">Nur die ersten 300 – nutze die Suche für weitere.</p>}
    </div>
  );
}
